// Kei.AI Service - Frontend API Client
// Calls the backend proxy server for secure authentication with Kei.AI

class KeiAIService {
  constructor() {
    this.baseURL = `${process.env.REACT_APP_API_URL || 'https://api.daggpt.network'}/api/kei-ai`;
    this.userId = null; // set via setUserId() before any generation call
  }

  /** Call this once after auth resolves so every request sends x-user-id */
  setUserId(id) { this.userId = id; }

  /**
   * Safe JSON fetch helper
   */
  async safeFetch(url, options = {}) {
    const defaultHeaders = {
      'Accept': 'application/json',
      // Always send userId so worker can charge the right account
      ...(this.userId ? { 'x-user-id': this.userId } : {}),
    };

    if (options.body && !options.headers?.['Content-Type']) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const mergedOptions = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      }
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 120s timeout for image generation

    try {
      const response = await fetch(url, {
        ...mergedOptions,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      console.log(`🌐 Kei.AI Fetch ${options.method || 'GET'} ${url} | Status: ${response.status}`);
      const contentType = response.headers.get('content-type');

      if (!response.ok) {
        let errorMessage = `Request failed with status ${response.status}`;
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const text = await response.text();
          errorMessage = text.substring(0, 100) || errorMessage;
        }
        throw new Error(errorMessage);
      }

      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      throw new Error(`Expected JSON response but received ${contentType || 'unknown content'}`);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out after 120 seconds');
      }
      throw error;
    }
  }

  /**
   * Fetch all jobs for the current user
   */
  async getJobs(options = {}) {
    try {
      const { status, type, limit = 50 } = options;
      let url = `${process.env.REACT_APP_API_URL || 'https://api.daggpt.network'}/api/jobs?limit=${limit}`;
      if (status) url += `&status=${status}`;
      if (type) url += `&type=${type}`;
      
      const response = await this.safeFetch(url);
      return response;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      return { success: false, jobs: [] };
    }
  }

  /**
   * Generate image with Nano Banana 2 (supports both text-to-image and image-to-image)
   */
  async generateImageNanoBanana2(prompt, options = {}) {
    try {
      const { 
        imageInput = [],
        aspectRatio = 'auto',
        resolution = '1K',
        outputFormat = 'jpg',
        referenceImage = null
      } = options;

      const isImageToImage = !!referenceImage || (imageInput && imageInput.length > 0);
      console.log(`🎨 Generating image with Nano Banana 2 (${isImageToImage ? 'Image-to-Image' : 'Text-to-Image'})...`);

      // Build image_input array
      let finalImageInput = imageInput;
      if (referenceImage && !imageInput.length) {
        finalImageInput = [referenceImage];
      }

      // Build request body
      const requestBody = {
        prompt,
        image_input: finalImageInput,
        aspect_ratio: aspectRatio,
        resolution,
        output_format: outputFormat
      };

      if (isImageToImage) {
        console.log('   Image Input:', finalImageInput);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/nano-banana-2/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Nano Banana 2 task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with Nano Banana 2:', error);
      throw error;
    }
  }

  /**
   * Generate image with Nano Banana Pro (supports both text-to-image and image-to-image)
   */
  async generateImageNanoBananaPro(prompt, options = {}) {
    try {
      const { 
        imageInput = [],
        aspectRatio = '1:1',
        resolution = '1K',
        outputFormat = 'png',
        referenceImage = null
      } = options;

      const isImageToImage = !!referenceImage || (imageInput && imageInput.length > 0);
      console.log(`🎨 Generating image with Nano Banana Pro (${isImageToImage ? 'Image-to-Image' : 'Text-to-Image'})...`);

      // Build image_input array
      let finalImageInput = imageInput;
      if (referenceImage && !imageInput.length) {
        finalImageInput = [referenceImage];
      }

      // Build request body
      const requestBody = {
        prompt,
        image_input: finalImageInput,
        aspect_ratio: aspectRatio,
        resolution,
        output_format: outputFormat
      };

      if (isImageToImage) {
        console.log('   Image Input:', finalImageInput);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/nano-banana-pro/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Nano Banana Pro task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with Nano Banana Pro:', error);
      throw error;
    }
  }

  /**
   * Generate image with Ideogram V3 (supports both text-to-image and edit)
   */
  async generateImageIdeogramV3(prompt, options = {}) {
    try {
      const { 
        renderingSpeed = 'BALANCED',
        style = 'AUTO',
        expandPrompt = true,
        imageSize = 'square_hd',
        seed = 123456,
        negativePrompt = 'blurry, low detail, distorted anatomy, extra limbs, malformed text, watermark',
        referenceImage = null,
        maskUrl = null
      } = options;

      const isEdit = !!(referenceImage && maskUrl);
      console.log(`🎨 Generating image with Ideogram V3 (${isEdit ? 'Edit' : 'Text-to-Image'})...`);

      // Build request body
      const requestBody = {
        prompt,
        rendering_speed: renderingSpeed,
        expand_prompt: expandPrompt,
        seed
      };

      // Add text-to-image specific parameters
      if (!isEdit) {
        requestBody.style = style;
        requestBody.image_size = imageSize;
        requestBody.negative_prompt = negativePrompt;
      }

      // Add edit specific parameters
      if (isEdit) {
        requestBody.image_url = referenceImage;
        requestBody.mask_url = maskUrl;
        console.log('   Reference image:', referenceImage);
        console.log('   Mask URL:', maskUrl);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/ideogram-v3/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Ideogram V3 task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with Ideogram V3:', error);
      throw error;
    }
  }

  /**
   * Generate image with Qwen Image (original) (supports both text-to-image and image-to-image)
   */
  async generateImageQwen(prompt, options = {}) {
    try {
      const { 
        imageSize = 'square_hd', 
        numInferenceSteps = 30, 
        guidanceScale = 2.5, 
        enableSafetyChecker = true, 
        outputFormat = 'png', 
        negativePrompt = ' ', 
        acceleration = 'none',
        strength = 0.8,
        referenceImage = null 
      } = options;

      const isImageToImage = !!referenceImage;
      console.log(`🎨 Generating image with Qwen Image (${isImageToImage ? 'Image-to-Image' : 'Text-to-Image'})...`);

      // Build request body
      const requestBody = {
        prompt,
        image_size: imageSize,
        num_inference_steps: numInferenceSteps,
        guidance_scale: guidanceScale,
        enable_safety_checker: enableSafetyChecker,
        output_format: outputFormat,
        negative_prompt: negativePrompt,
        acceleration
      };

      // Add image-to-image specific parameters
      if (isImageToImage) {
        requestBody.image_url = referenceImage;
        requestBody.strength = strength;
        console.log('   Reference image:', referenceImage);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/qwen/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Qwen Image task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with Qwen Image:', error);
      throw error;
    }
  }

  /**
   * Generate image with Seedream 4.0 (supports both text-to-image and edit)
   */
  async generateImageSeedream40(prompt, options = {}) {
    try {
      const { imageSize = 'square_hd', imageResolution = '1K', maxImages = 1, seed = 50331296, nsfwChecker = true, referenceImage = null } = options;

      const isEdit = !!referenceImage;
      console.log(`🎨 Generating image with Seedream 4.0 (${isEdit ? 'Edit' : 'Text-to-Image'})...`);

      // Build request body
      const requestBody = {
        prompt,
        image_size: imageSize,
        image_resolution: imageResolution,
        max_images: maxImages,
        seed,
        nsfw_checker: nsfwChecker
      };

      // Add image_urls for edit
      if (isEdit) {
        requestBody.image_urls = [referenceImage];
        console.log('   Reference image:', referenceImage);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/seedream-4.0/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Seedream 4.0 task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with Seedream 4.0:', error);
      throw error;
    }
  }

  /**
   * Generate image with Grok Imagine (supports both text-to-image and image-to-image)
   */
  async generateImageGrokImagine(prompt, options = {}) {
    try {
      const { imageSize = '3:2', referenceImage = null } = options;

      const isImageToImage = !!referenceImage;
      console.log(`🎨 Generating image with Grok Imagine (${isImageToImage ? 'Image-to-Image' : 'Text-to-Image'})...`);

      // Build request body
      const requestBody = {
        prompt
      };

      // Add aspect_ratio for text-to-image only
      if (!isImageToImage) {
        requestBody.aspect_ratio = imageSize;
      }

      // Add image_urls for image-to-image
      if (isImageToImage) {
        requestBody.image_urls = [referenceImage];
        console.log('   Reference image:', referenceImage);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/grok-imagine/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Grok Imagine task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with Grok Imagine:', error);
      throw error;
    }
  }

  /**
   * Internal helper - call the backend FLUX-2 route (variant: 'flex' or 'pro').
   *   /api/kei-ai/flux-2/generate     -> Flex (standard)
   *   /api/kei-ai/flux-2-pro/generate -> Pro
   */
  async _generateImageFlux2Variant(variant, prompt, options = {}) {
    const { imageSize = '1:1', resolution = '1K', nsfwChecker = false, referenceImage = null } = options;
    const isImageToImage = !!referenceImage;
    const route = variant === 'pro' ? 'flux-2-pro' : 'flux-2';

    console.log(`🎨 Generating image with FLUX-2 ${variant.toUpperCase()} (${isImageToImage ? 'I2I' : 'T2I'})...`);

    const requestBody = {
      prompt,
      aspect_ratio: imageSize,
      resolution,
      nsfw_checker: nsfwChecker,
    };
    if (isImageToImage) {
      requestBody.input_urls = Array.isArray(referenceImage) ? referenceImage : [referenceImage];
    }

    const createResponse = await this.safeFetch(`${this.baseURL}/${route}/generate`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
    if (!createResponse.success || !createResponse.taskId) {
      throw new Error('Failed to create image generation task');
    }
    const taskId = createResponse.taskId;
    console.log(`✅ FLUX-2 ${variant.toUpperCase()} task created: ${taskId}`);
    return await this.pollTaskStatus(taskId);
  }

  /**
   * Generate image with FLUX-2 (Flex / standard tier).
   */
  async generateImageFlux2(prompt, options = {}) {
    try {
      return await this._generateImageFlux2Variant('flex', prompt, options);
    } catch (error) {
      console.error('Error generating image with FLUX-2:', error);
      throw error;
    }
  }

  /**
   * Generate image with FLUX-2 Pro.
   */
  async generateImageFlux2Pro(prompt, options = {}) {
    try {
      return await this._generateImageFlux2Variant('pro', prompt, options);
    } catch (error) {
      console.error('Error generating image with FLUX-2 Pro:', error);
      throw error;
    }
  }

  /**
   * Generate image with GPT Image-2 (T2I + I2I).
   */
  async generateImageGPT2(prompt, options = {}) {
    try {
      const { imageSize = '1:1', quality = 'medium', referenceImage = null } = options;
      const isImageToImage = !!referenceImage;
      console.log(`🎨 Generating image with GPT Image-2 (${isImageToImage ? 'I2I' : 'T2I'})...`);

      const requestBody = {
        prompt,
        aspect_ratio: imageSize,
        quality,
      };
      if (isImageToImage) {
        requestBody.input_urls = Array.isArray(referenceImage) ? referenceImage : [referenceImage];
      }

      const createResponse = await this.safeFetch(`${this.baseURL}/gpt-image-2/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }
      const taskId = createResponse.taskId;
      console.log(`✅ GPT Image-2 task created: ${taskId}`);
      return await this.pollTaskStatus(taskId);
    } catch (error) {
      console.error('Error generating image with GPT Image-2:', error);
      throw error;
    }
  }

  /**
   * Generate image with Seedream 4.5 Pro (supports both text-to-image and edit)
   */
  async generateImageSeedream45(prompt, options = {}) {
    try {
      const { imageSize = '1:1', quality = 'basic', nsfwChecker = false, referenceImage = null } = options;

      const isEdit = !!referenceImage;
      console.log(`🎨 Generating image with Seedream 4.5 Pro (${isEdit ? 'Edit' : 'Text-to-Image'})...`);

      // Build request body
      const requestBody = {
        prompt,
        aspect_ratio: imageSize,
        quality,
        nsfw_checker: nsfwChecker
      };

      // Add image_urls for edit
      if (isEdit) {
        requestBody.image_urls = [referenceImage];
        console.log('   Reference image:', referenceImage);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/seedream-4.5/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Seedream 4.5 Pro task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with Seedream 4.5 Pro:', error);
      throw error;
    }
  }

  /**
   * Generate image with Qwen 2 (supports both text-to-image and image-edit)
   */
  async generateImageQwen2(prompt, options = {}) {
    try {
      const { imageSize = '16:9', seed = 0, outputFormat = 'png', referenceImage = null } = options;

      const isImageEdit = !!referenceImage;
      console.log(`🎨 Generating image with Qwen 2 (${isImageEdit ? 'Image-Edit' : 'Text-to-Image'})...`);

      // Build request body
      const requestBody = {
        prompt,
        image_size: imageSize,
        seed,
        output_format: outputFormat
      };

      // Add image_url for image-edit
      if (isImageEdit) {
        requestBody.image_url = referenceImage;
        console.log('   Reference image:', referenceImage);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/qwen2/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Qwen 2 task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with Qwen 2:', error);
      throw error;
    }
  }

  /**
   * Generate image with Seedream 5.0 Lite (supports both text-to-image and image-to-image)
   */
  async generateImageSeedream50Lite(prompt, options = {}) {
    try {
      const { imageSize = '1:1', quality = 'basic', nsfwChecker = true, referenceImage = null } = options;

      const isImageToImage = !!referenceImage;
      console.log(`🎨 Generating image with Seedream 5.0 Lite (${isImageToImage ? 'Image-to-Image' : 'Text-to-Image'})...`);

      // Build request body
      const requestBody = {
        prompt,
        aspect_ratio: imageSize,
        quality,
        nsfw_checker: nsfwChecker
      };

      // Add image_urls for image-to-image
      if (isImageToImage) {
        requestBody.image_urls = [referenceImage];
        console.log('   Reference image:', referenceImage);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/seedream-5.0-lite/generate`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Seedream 5.0 Lite task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with Seedream 5.0 Lite:', error);
      throw error;
    }
  }

  /**
   * Generate image with GPT Image-1.5 (supports both text-to-image and image-to-image)
   */
  async generateImageGPT15(prompt, options = {}) {
    try {
      const { imageSize = '1:1', quality = 'medium', referenceImage = null } = options;

      const isImageToImage = !!referenceImage;
      console.log(`🎨 Generating image with GPT Image-1.5 (${isImageToImage ? 'Image-to-Image' : 'Text-to-Image'})...`);

      // Build request body
      const requestBody = {
        prompt,
        aspect_ratio: imageSize,
        quality
      };

      // Add input_urls for image-to-image
      if (isImageToImage) {
        requestBody.input_urls = [referenceImage];
        console.log('   Reference image:', referenceImage);
      }

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/gpt-image-1.5/text-to-image`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ GPT Image-1.5 task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error('Error generating image with GPT Image-1.5:', error);
      throw error;
    }
  }

  /**
   * Generate image using any Kei.AI model (unified endpoint)
   */
  async generateImageKeiAI(modelId, prompt, options = {}) {
    try {
      const { imageSize = '16:9', seed = 0, outputFormat = 'png' } = options;

      console.log(`🎨 Generating image with Kei.AI model: ${modelId}...`);

      // Step 1: Create task
      const createResponse = await this.safeFetch(`${this.baseURL}/generate`, {
        method: 'POST',
        body: JSON.stringify({
          modelId,
          prompt,
          image_size: imageSize,
          seed,
          output_format: outputFormat
        })
      });

      if (!createResponse.success || !createResponse.taskId) {
        throw new Error('Failed to create image generation task');
      }

      const taskId = createResponse.taskId;
      console.log(`✅ Kei.AI task created: ${taskId}`);

      // Step 2: Poll for completion
      const result = await this.pollTaskStatus(taskId);
      return result;

    } catch (error) {
      console.error(`Error generating image with ${modelId}:`, error);
      throw error;
    }
  }

  /**
   * Listen for task completion via Server-Sent Events (SSE)
   * Falls back to polling if connection drops
   */
  pollTaskStatus(taskId, modelId = '', maxAttempts = 180, intervalMs = 2000, signal) {
    return new Promise((resolve, reject) => {
      const unifiedBaseUrl = this.baseURL.replace('/api/kei-ai', '/api/unified');
      const sseUrl = `${unifiedBaseUrl}/events/${taskId}`;
      console.log(`🌐 Connecting to SSE for task ${taskId}: ${sseUrl}`);
      
      const eventSource = new EventSource(sseUrl);
      let isResolved = false;
      
      let sseTimeout = setTimeout(() => {
        if (isResolved) return;
        eventSource.close();
        console.warn(`⚠️ SSE timeout for task ${taskId}, relying entirely on polling...`);
      }, maxAttempts * intervalMs);

      const cleanup = () => {
        isResolved = true;
        clearTimeout(sseTimeout);
        eventSource.close();
      };

      if (signal) {
        if (signal.aborted) {
          cleanup();
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }
        signal.addEventListener('abort', () => {
          cleanup();
          reject(new DOMException('Aborted', 'AbortError'));
        });
      }

      eventSource.onmessage = (event) => {
        if (isResolved) return;
        try {
          const data = JSON.parse(event.data);
          if (data.status === 'connected') {
            console.log(`📡 SSE Connected for task ${taskId}`);
          } else if (data.status === 'completed' && data.imageUrl) {
            console.log(`✅ Task ${taskId} completed via Webhook!`);
            cleanup();
            resolve({ success: true, imageUrl: data.imageUrl, taskId: taskId, allSunoTracks: data.allSunoTracks || data.taskData?.allSunoTracks });
          } else if (data.status === 'failed') {
            cleanup();
            reject(new Error(data.error || 'Generation failed via Webhook'));
          }
        } catch (err) {
          console.error('Failed to parse SSE data', err);
        }
      };

      eventSource.onerror = (err) => {
        if (isResolved) return;
        console.warn(`⚠️ SSE connection error for task ${taskId}.`);
        eventSource.close();
      };

      // HYBRID APPROACH: Run a slow background poll specifically for Localhost 
      // where Webhooks cannot be delivered by Kie.ai
      let pollAttempts = 0;
      let pollErrorCount = 0;
      const slowPollInterval = 10000; // Poll every 10 seconds
      
      const doBackgroundPoll = async () => {
        while (!isResolved && pollAttempts < maxAttempts) {
          try {
            await new Promise(r => setTimeout(r, slowPollInterval));
            if (isResolved) break;
            
            const query = modelId ? `?modelId=${encodeURIComponent(modelId)}` : '';
            const statusData = await this.safeFetch(`${unifiedBaseUrl}/task-status/${taskId}${query}`);
            
            pollErrorCount = 0; // Reset on success
            console.log(`🔄 Hybrid Polling task ${taskId} - Status: ${statusData.status}`);
            
            if (statusData.status === 'completed' && statusData.imageUrl) {
              console.log(`✅ Task ${taskId} completed via Hybrid Polling`);
              cleanup();
              resolve({ success: true, imageUrl: statusData.imageUrl, taskId: taskId, allSunoTracks: statusData.allSunoTracks || statusData.taskData?.allSunoTracks });
              break;
            } else if (statusData.status === 'failed') {
              cleanup();
              reject(new Error(statusData.error || 'Generation failed via Polling'));
              break;
            }
            pollAttempts++;
          } catch (e) {
            console.error(`Hybrid poll error for task ${taskId}:`, e);
            pollErrorCount++;
            if (pollErrorCount >= 3) {
              cleanup();
              reject(new Error(`Polling interrupted: ${e.message}`));
              break;
            }
          }
        }
      };
      
      doBackgroundPoll();
    });
  }

  /**
   * Legacy Polling logic as a bulletproof fallback
   */
  async fallbackPollTaskStatus(taskId, modelId = '', maxAttempts = 180, intervalMs = 2000) {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        const query = modelId ? `?modelId=${encodeURIComponent(modelId)}` : '';
        const unifiedBaseUrl = this.baseURL.replace('/api/kei-ai', '/api/unified');
        const statusData = await this.safeFetch(`${unifiedBaseUrl}/task-status/${taskId}${query}`);
        
        console.log(`🔄 Polling task ${taskId} - Status: ${statusData.status} (Attempt ${attempts + 1}/${maxAttempts})`);

        if (statusData.status === 'completed' && statusData.imageUrl) {
          console.log(`✅ Task ${taskId} completed successfully`);
          return {
            success: true,
            imageUrl: statusData.imageUrl,
            taskId: taskId
          };
        }

        if (statusData.status === 'failed') {
          throw new Error(statusData.error || 'Image generation failed');
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        attempts++;
      } catch (error) {
        console.error(`Error polling task ${taskId}:`, error);
        throw error;
      }
    }

    throw new Error(`Task ${taskId} timed out after ${maxAttempts * intervalMs / 1000} seconds`);
  }

  /**
   * Generate image - Universal method that routes to appropriate model
   */
  async generateImage(modelId, prompt, options = {}) {
    // All standard Kei.AI models use the unified endpoint
    return await this.generateImageKeiAI(modelId, prompt, options);
  }
}

const keiAIService = new KeiAIService();
export default keiAIService;
